import {
  Box, Button, Card, CardBody, CardHeader, Heading, HStack, Icon,
  SimpleGrid, Text, VStack, Badge, Menu, MenuButton, MenuList, MenuItem,
} from '@chakra-ui/react';
import { FiActivity, FiPlus, FiSettings, FiList, FiChevronDown } from 'react-icons/fi';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/design-system/components/LoadingSpinner';
import { RuntimeStatsCard, TopFunctionsTable, AlertsBadge } from '../components';
import { Modal } from '@/design-system/components/Modal';
import { FormField } from '@/components/Modal/FormField';
import { ProfileStatus, ProfileType } from '@/lib/sdk/profiling/types';
import { useProfilingPage } from '../hooks';

export function ProfilingPage() {
  const p = useProfilingPage();

  if (!p.isSuperAdmin) {
    return (
      <PageContainer title="Access Denied">
        <Box textAlign="center" py={12}>
          <Icon as={FiActivity} boxSize={16} color="red.500" mb={4} />
          <Heading size="md" color="text-primary" mb={2}>Super Admin Access Required</Heading>
          <Text color="text-secondary">This page is only accessible to super administrators.</Text>
        </Box>
      </PageContainer>
    );
  }

  if (p.isLoadingStats && p.isLoadingProfiles) {
    return <LoadingSpinner message="Loading profiling dashboard..." />;
  }

  return (
    <PageContainer
      title="Performance Profiling"
      description="Monitor and analyze application performance"
      action={
        <HStack spacing={3}>
          <Button leftIcon={<FiList />} variant="outline" onClick={() => p.navigate('profiles')} borderColor="border-default" _hover={{ borderColor: 'border-hover' }}>
            View All Profiles
          </Button>
          <Button leftIcon={<FiSettings />} variant="outline" onClick={() => p.navigate('settings')} borderColor="border-default" _hover={{ borderColor: 'border-hover' }}>
            Settings
          </Button>
          <Menu>
            <MenuButton as={Button} colorScheme="brand" leftIcon={<FiPlus />} rightIcon={<FiChevronDown />}>
              Collect Profile
            </MenuButton>
            <MenuList>
              {Object.values(ProfileType).map((type) => (
                <MenuItem key={type} onClick={() => p.openForType(type)}>
                  {type.charAt(0).toUpperCase() + type.slice(1)} Profile
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>
      }
    >
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="md" color="text-primary" mb={4}>Runtime Statistics</Heading>
          <RuntimeStatsCard stats={p.runtimeStats} isLoading={p.isLoadingStats} />
        </Box>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
            <CardBody>
              <VStack align="start" spacing={2}>
                <Text color="text-secondary" fontSize="sm">Total Profiles</Text>
                <HStack>
                  <Heading size="lg" color="text-primary">{p.profilesData?.total || 0}</Heading>
                  <Badge colorScheme="brand">All Time</Badge>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
            <CardBody>
              <VStack align="start" spacing={2}>
                <Text color="text-secondary" fontSize="sm">Latest Profile</Text>
                {p.latestProfile ? (
                  <HStack>
                    <Badge colorScheme="purple">{p.latestProfile.profile_type.toUpperCase()}</Badge>
                    <Badge colorScheme={p.latestProfile.status === ProfileStatus.COMPLETED ? 'green' : p.latestProfile.status === ProfileStatus.COLLECTING ? 'blue' : p.latestProfile.status === ProfileStatus.FAILED ? 'red' : 'gray'}>
                      {p.latestProfile.status}
                    </Badge>
                  </HStack>
                ) : (
                  <Text color="text-muted" fontSize="sm">No profiles yet</Text>
                )}
              </VStack>
            </CardBody>
          </Card>

          <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
            <CardBody>
              <VStack align="start" spacing={2}>
                <Text color="text-secondary" fontSize="sm">Goroutine Status</Text>
                <HStack>
                  <Heading size="lg" color="text-primary">{p.runtimeStats?.goroutine_count.toLocaleString() || '-'}</Heading>
                  <Badge colorScheme={(p.runtimeStats?.goroutine_count || 0) > 10000 ? 'red' : (p.runtimeStats?.goroutine_count || 0) > 5000 ? 'orange' : 'green'}>
                    {(p.runtimeStats?.goroutine_count || 0) > 10000 ? 'High' : (p.runtimeStats?.goroutine_count || 0) > 5000 ? 'Moderate' : 'Normal'}
                  </Badge>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {p.selectedProfileId && (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
              <CardHeader><Heading size="sm" color="text-primary">Top Functions</Heading></CardHeader>
              <CardBody pt={0}>
                {p.isLoadingAnalysis ? <LoadingSpinner /> : p.analysisData?.top_functions ? (
                  <TopFunctionsTable functions={p.analysisData.top_functions} maxItems={10} />
                ) : <Text color="text-muted" textAlign="center" py={4}>No function data available</Text>}
              </CardBody>
            </Card>

            <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
              <CardHeader><Heading size="sm" color="text-primary">Alerts & Insights</Heading></CardHeader>
              <CardBody pt={0}>
                {p.isLoadingAnalysis ? <LoadingSpinner /> : (
                  <VStack spacing={4} align="stretch">
                    <AlertsBadge alerts={p.analysisData?.alerts || []} />
                    {p.analysisData?.insights && p.analysisData.insights.length > 0 && (
                      <Box>
                        <Text color="text-secondary" fontSize="sm" mb={2}>Insights</Text>
                        <VStack align="start" spacing={1}>
                          {p.analysisData.insights.map((insight, index) => (
                            <Text key={index} color="text-primary" fontSize="sm">{insight}</Text>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                )}
              </CardBody>
            </Card>
          </SimpleGrid>
        )}

        <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="sm" color="text-primary">Recent Profiles</Heading>
              <Button size="sm" variant="ghost" onClick={() => p.navigate('profiles')}>View All</Button>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            {p.isLoadingProfiles ? <LoadingSpinner /> : p.profilesData?.items && p.profilesData.items.length > 0 ? (
              <VStack spacing={3} align="stretch">
                {p.profilesData.items.map((profile) => (
                  <Box key={profile.id} p={3} borderWidth="1px" borderColor="border-default" borderRadius="md" cursor="pointer" _hover={{ borderColor: 'brand.500' }} onClick={() => p.navigate(`profiles/${profile.id}`)}>
                    <HStack justify="space-between">
                      <HStack spacing={3}>
                        <Badge colorScheme="purple">{profile.profile_type.toUpperCase()}</Badge>
                        <Text color="text-primary" fontSize="sm">{profile.duration_seconds}s</Text>
                        <Badge colorScheme={profile.status === ProfileStatus.COMPLETED ? 'green' : profile.status === ProfileStatus.COLLECTING ? 'blue' : profile.status === ProfileStatus.FAILED ? 'red' : 'gray'}>
                          {profile.status}
                        </Badge>
                      </HStack>
                      <Text color="text-muted" fontSize="xs">{new Date(profile.created_at).toLocaleString()}</Text>
                    </HStack>
                    {profile.description && <Text color="text-secondary" fontSize="sm" mt={2} noOfLines={1}>{profile.description}</Text>}
                  </Box>
                ))}
              </VStack>
            ) : <Text color="text-muted" textAlign="center" py={4}>No profiles collected yet. Click "Collect Profile" to get started.</Text>}
          </CardBody>
        </Card>
      </VStack>

      <Modal
        isOpen={p.isOpen}
        onClose={p.onClose}
        title="Collect Profile"
        footer={
          <HStack spacing={3}>
            <Button variant="outline" onClick={p.onClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={p.onCollectSubmit} isLoading={p.isCollecting}>Start Collection</Button>
          </HStack>
        }
      >
        <VStack spacing={4} align="stretch">
          <FormField label="Profile Type" name="profile_type" register={p.register} error={p.errors.profile_type} type="select" options={p.profileTypeOptions} required />
          <FormField label="Duration (seconds)" name="duration_seconds" register={p.register} error={p.errors.duration_seconds} type="number" min={5} max={300} description="How long to collect the profile (5-300 seconds)" />
          <FormField label="Description" name="description" register={p.register} error={p.errors.description} type="textarea" placeholder="Optional description for this profile" maxLength={500} />
        </VStack>
      </Modal>
    </PageContainer>
  );
}
